import { Flex, Grid, Group, Stack, Text } from '@mantine/core';
import { utils, writeFile } from 'xlsx';
import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import '../../scss/vendors/bstyles.scss';
import Button from '../../components/elements/Button';
import { useMutation } from '@tanstack/react-query';
import { showNotification } from '../../utilities/mantineNotification';
import Card from '../../components/elements/Card';
import dayjs from 'dayjs';
import id from 'dayjs/locale/id';
import { dateFormat } from '../../utilities/dataFormat';
import * as Unicons from '@iconscout/react-unicons';
import ChartFilter from './ChartFilter';
import { useDisclosure } from '@mantine/hooks';
import ModalChartTraining from '../../components/fragments/ModalChart';
import BarChart from '../../components/elements/BarChart';
import LoaderChart from './LoaderChart';
import { HiChevronRight } from 'react-icons/hi';
import useLocalStorage from '../../hooks/useUserData';
import {
  downloadChartTSI,
  getChartTSI
} from '../../services/dashboardPrincipal';
import ModalDownload from './ModalDownload';

ChartJS.register(
  ChartDataLabels,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export interface DataDownloadReportTraining {
  courseId: string;
  courseName: string;
  completionDate: string;
  enrolmentDate: string;
  enrolmentMethod: string;
  buName: null | string;
  subBuName: null | string;
  deptName: null | string;
  binusianId: null | string;
  name: null | string;
  buCode: null | string;
  subBuCode: null | string;
  deptCode: null | string;
  sectionCode: null | string;
  sectionName: null | string;
  subSectionCode: null | string;
  subSectionName: null | string;
}

export interface ChartTrainingProps {
  startDate: string;
  endDate: string;
  title: string;
  businessUnit?: string;
  subBusinessUnit?: string;
  departement?: string;
  buCode?: null | string;
  subBuCode?: null | string;
  deptCode?: null | string;
}

const ChartTSI: React.FC<ChartTrainingProps> = ({
  title,
  endDate,
  startDate,
  businessUnit,
  subBusinessUnit,
  departement,
  buCode,
  subBuCode,
  deptCode
}) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [openedDownload, { toggle: toggleDownload }] = useDisclosure(false);
  const [chartModal, setChartModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [chartFilter, setChartFilter] = useState({
    startDate: null,
    endDate: null,
    bulan: null,
    tahun: null,
    deptName: null,
    sectionName: null,
    deptCode: null,
    sectionCode: null
  });
  const [chartLevel, setChartLevel] = useState(0);
  const [currentData, setCurrentData] = useState(null);
  const [chartConfig, setChartConfig] = useState([]);
  const [statusFMSTR, setStatusFMSTR] = useState('0');
  const [userData, setUserData] = useLocalStorage('persist:root', null);
  const data = JSON.parse(userData?.auth);

  const fetchedDataChart = useMutation({
    mutationFn: () =>
      getChartTSI({
        StartDate: chartFilter.startDate,
        EndDate: chartFilter.endDate,
        BinusianId: data?.userData?.binusianId,
        Bulan: chartFilter.bulan,
        Tahun: chartFilter.tahun,
        DeptName: chartFilter.deptName,
        SectionName: chartFilter.sectionName,
        DeptCode: chartFilter.deptCode,
        SectionCode: chartFilter.sectionCode
      }),
    onError: (error: any) => {
      showNotification({
        type: 'error',
        message: error
      });
    }
  });

  const fetchedDownloadChart = useMutation({
    mutationFn: () =>
      downloadChartTSI({
        StartDate: chartFilter.startDate,
        EndDate: chartFilter.endDate,
        Bulan: chartFilter.bulan,
        BinusianId: data?.userData?.binusianId,
        Tahun: chartFilter.tahun,
        DeptCode: chartFilter.deptCode,
        SectionCode: chartFilter.sectionCode,
        DeptName: chartFilter.deptName,
        SectionName: chartFilter.sectionName
      }),
    onSuccess: (data) => {
      if (data?.data?.data?.length > 0) {
        showNotification({
          type: 'success',
          message: 'Download in progress, please wait a moment.'
        });
        renderConvertExcel(data?.data?.data);
        return;
      }

      showNotification({
        type: 'error',
        message: `Data download is empty. Please try again later.`
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        message: error
      });
    }
  });

  const renderConvertExcel = (data: DataDownloadReportTraining[]) => {
    const formatFilename = () => {
      const parts = [`Chart TSI - download at ${dayjs().toLocaleString()}`];
      return parts.filter(Boolean).join('_');
    };

    const filename = formatFilename();
    const wb = utils.book_new();

    // Convert the JSON data to a worksheet
    const ws = utils.json_to_sheet(data);

    // Add the worksheet to the workbook
    utils.book_append_sheet(wb, ws, 'Sheet1');

    // Write the workbook to a file
    // This will trigger a file download in the browser
    writeFile(wb, `${filename}.xlsx`);
  };

  useEffect(() => {
    setChartFilter((prevState) => ({
      ...prevState,
      startDate,
      endDate,
      deptCode
    }));
  }, [endDate, startDate, deptCode]);

  useEffect(() => {
    if (chartFilter && chartFilter.startDate && chartFilter.endDate) {
      const commonFields = {
        startDate: chartFilter.startDate,
        endDate: chartFilter.endDate,
        bulan: chartFilter.bulan,
        tahun: chartFilter.tahun,
        deptName: chartFilter.deptName,
        sectionName: chartFilter.sectionName,
        deptCode: chartFilter.deptCode,
        sectionCode: chartFilter.sectionCode
      };

      const levelConfig = [
        {},
        {},
        {
          deptName: chartFilter.deptName,
          deptCode: chartFilter.deptCode,
          sectionName: chartFilter.sectionName,
          sectionCode: chartFilter.sectionCode
        },
        {
          deptName: chartFilter.deptName,
          deptCode: chartFilter.deptCode,
          sectionName: chartFilter.sectionName,
          sectionCode: chartFilter.sectionCode
        }
      ];

      if (
        chartLevel >= 1 &&
        chartLevel <= 5 &&
        chartConfig.length === chartLevel - 1
      ) {
        setChartConfig((prevState) => [
          ...prevState,
          { ...commonFields, ...levelConfig[chartLevel] }
        ]);
      }
    }
  }, [chartFilter, chartLevel, chartConfig.length]);

  useEffect(() => {
    fetchedDataChart.mutate();
  }, [
    chartFilter?.bulan,
    chartFilter?.tahun,
    chartFilter?.startDate,
    chartFilter?.endDate,
    chartFilter?.deptName,
    chartFilter?.sectionName
  ]);

  const onClick = (element) => {
    if (element.length > 0) {
      if (chartLevel === 0) {
        setChartModal(true);
      }
      if (chartModal) {
        const maxLevel = 3;
        if (chartLevel < maxLevel) {
          setChartLevel((prevLevel) => prevLevel + 1);
        }
        const index = element[0].index;
        // Get the label of the clicked bar
        const item = fetchedDataChart?.data?.data?.data[index];

        if (chartLevel < maxLevel) {
          setChartFilter((prevState) => ({
            ...prevState,
            bulan: item.month.toString(),
            tahun: item.year.toString(),
            deptName: chartLevel === 1 ? item.label : prevState.deptName,
            sectionName: chartLevel === 2 ? item.label : prevState.sectionName,
            deptCode: chartLevel === 1 ? item.code : prevState.deptCode,
            sectionCode: chartLevel === 2 ? item.code : prevState.sectionCode
          }));
        }
      }
    }
  };

  const handleCloseModal = () => {
    setChartFilter((prevState) => ({
      ...prevState,
      bulan: null,
      tahun: null,
      deptName: null,
      sectionName: null,
      deptCode: null,
      sectionCode: null
    }));

    setLoading(true);

    setTimeout(() => {
      setChartLevel(0);
      setChartConfig([]);
      setChartModal(false);
      setLoading(false);
      fetchedDataChart.mutate();
    }, 500);
  };

  useEffect(() => {
    setChartFilter((prevState) => ({
      ...prevState,
      bulan: chartConfig[chartLevel - 1]?.bulan,
      tahun: chartConfig[chartLevel - 1]?.tahun,
      deptCode: chartConfig[chartLevel - 1]?.deptCode,
      sectionCode: chartConfig[chartLevel - 1]?.sectionCode,
      deptName: chartConfig[chartLevel - 1]?.deptName,
      sectionName: chartConfig[chartLevel - 1]?.sectionName
    }));
    setCurrentData(chartConfig[chartLevel - 1]);
  }, [chartConfig, chartLevel]);

  const handleBack = () => {
    setLoading(true);
    if (chartLevel - 1 === 0) {
      setChartFilter((prevState) => ({
        ...prevState,
        bulan: null,
        tahun: null,
        deptName: null,
        deptCode: null,
        sectionName: null,
        sectionCode: null
      }));
      fetchedDataChart.mutate();
    }
    setTimeout(() => {
      setChartLevel((prevState) => prevState - 1);
      setChartConfig((prevState) => prevState.slice(0, -1));
      setLoading(false);
    }, 500);
  };

  const handleDownload = () => fetchedDownloadChart.mutate();

  const renderContent = () => {
    return (
      <BarChart
        key={chartLevel.toString()}
        chartKeys={['rating']}
        chartLabels={['Rating']}
        chartColors={['#7B2183']}
        data={fetchedDataChart?.data?.data?.data || []}
        onClick={onClick}
        loading={fetchedDataChart.isLoading}
        isError={fetchedDataChart.isError}
        onLoad={() => fetchedDataChart.mutate()}
        withLegend
      />
    );
  };

  const handleBreadcrumb = (item) => {
    setLoading(true);
    setTimeout(() => {
      setChartLevel(item.level);
      setChartConfig((prevState) => prevState.slice(0, item.level));
      setLoading(false);
    }, 500);
  };

  const renderModalBreadcrumb = () => {
    if (chartLevel <= 0) return null;

    const breadcrumbData = [
      {
        title: currentData?.bulan
          ? dayjs()
              .month(currentData.bulan - 1)
              .locale(id)
              .format('MMMM')
          : '',
        level: 1
      },
      {
        title: currentData?.deptName || '',
        level: 2
      },
      {
        title: currentData?.sectionName || '',
        level: 3
      }
    ];

    const filteredItems = breadcrumbData.filter((item) => item.title);

    return (
      <Group gap={0}>
        {filteredItems.map((item, index) => (
          <React.Fragment key={index}>
            <Button
              onClick={() => handleBreadcrumb(item)}
              variant="transparent"
            >
              {item.title}
            </Button>
            {index !== filteredItems.length - 1 && <HiChevronRight />}
          </React.Fragment>
        ))}
      </Group>
    );
  };

  const onDownload = () => toggleDownload();

  const handleConfirmDownload = () => {
    setTimeout(() => {
      toggleDownload();
      handleDownload();
    }, 500);
  };

  return (
    <>
      <Grid.Col span={{ base: 12, md: 4 }}>
        {fetchedDataChart.isLoading && <LoaderChart />}
        {!fetchedDataChart.isLoading && (
          <Card h={'100%'}>
            <Flex gap={16} justify={'space-between'}>
              <Stack gap={0}>
                <Text className="text-sm-semibold">{title}</Text>
                <Text className="text-xs-medium" c={'gray'}>
                  Period{' '}
                  {dayjs(chartFilter.startDate)
                    .locale(id)
                    .format(dateFormat('dateyear'))}{' '}
                  -{' '}
                  {dayjs(chartFilter.endDate)
                    .locale(id)
                    .format(dateFormat('dateyear'))}
                </Text>
              </Stack>

              <Group>
                {fetchedDataChart.isError && (
                  <Button
                    variant="outline"
                    size="sm"
                    styles={{
                      padding: '6px 10px',
                      borderColor: '#CCCCCC',
                      color: '#333333',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12
                    }}
                    onClick={() => fetchedDataChart.mutate()}
                  >
                    <Unicons.UilRefresh size={14} />
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  styles={{
                    padding: '6px 10px',
                    borderColor: '#CCCCCC',
                    color: '#333333',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12
                  }}
                  onClick={() => open()}
                >
                  <Unicons.UilFilter size={14} />
                </Button>
              </Group>
            </Flex>
            <Flex mt={16}>{renderContent()}</Flex>
          </Card>
        )}
      </Grid.Col>
      <ChartFilter
        isVisible={opened}
        downloadable
        onDownload={onDownload}
        onClose={() => close()}
        loading={fetchedDownloadChart.isLoading}
        endDate={chartFilter.endDate}
        startDate={chartFilter.startDate}
        onFilter={(data) =>
          setChartFilter((prevState) => ({ ...prevState, ...data }))
        }
      />

      <ModalChartTraining
        title={title}
        description={renderModalBreadcrumb()}
        loading={loading}
        isVisible={chartModal}
        onClose={handleCloseModal}
        backButton={chartLevel > 0}
        onBack={handleBack}
        downloadable
        onDownload={onDownload}
        loadingDownload={fetchedDownloadChart.isLoading}
        chart={renderContent()}
      />

      <ModalDownload
        opened={openedDownload}
        title={`Download ${title}`}
        onClose={() => toggleDownload()}
        onConfirm={handleConfirmDownload}
      />
    </>
  );
};

export default ChartTSI;
